import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Crown, Heart, X } from "lucide-react";
import { toast } from "sonner";

export default function WhoLikedMe() {
  const [items, setItems] = useState(null);
  const [gated, setGated] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/likes/received");
      setItems(data);
      setGated(false);
    } catch (err) {
      if (err.response?.status === 402) setGated(true);
      else toast.error("Couldn't load likes");
    }
  };
  useEffect(() => { load(); }, []);

  const swipe = async (target_user_id, action) => {
    try {
      const { data } = await api.post("/swipe", { target_user_id, action });
      if (data.match) toast.success("It's a match!");
      setItems((cur) => (cur || []).filter((x) => x.user.user_id !== target_user_id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't respond");
    }
  };

  if (gated) {
    return (
      <div className="min-h-screen bg-[#FFFDF9]">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 py-10">
          <Card className="p-10 text-center rounded-3xl border-[#F5D77E] bg-[#FEF3D6]/40" data-testid="likes-premium-gate">
            <Crown className="w-10 h-10 text-[#B45309] mx-auto mb-3" />
            <h1 className="font-serif text-3xl font-bold text-[#2C2623] mb-2">See who liked you</h1>
            <p className="text-[#6E655F] mb-6 max-w-md mx-auto">
              Every incoming like appears here — instantly. It's part of HerpMates Premium.
            </p>
            <Button
              onClick={() => navigate("/premium")}
              data-testid="likes-upgrade-btn"
              className="h-11 px-6 rounded-full bg-[#D95D39] hover:bg-[#C24C29] font-semibold">
              Try Premium
            </Button>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#2C2623]">Who liked you</h1>
          <Badge className="bg-[#FEF3D6] text-[#B45309] hover:bg-[#FEF3D6]">Premium</Badge>
        </div>
        <p className="text-[#6E655F] mb-8">Tap to like back and match, or pass to remove.</p>

        {!items ? (
          <div className="text-[#6E655F]">Loading…</div>
        ) : items.length === 0 ? (
          <Card className="p-10 text-center rounded-3xl border-[#E8DEC8] bg-white">
            <p className="text-[#6E655F]">No one has liked you yet. New likes will appear here.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(({ user, liked_at }) => (
              <Card key={user.user_id} className="p-4 rounded-2xl border-[#E8DEC8] bg-white"
                data-testid={`liked-me-card-${user.user_id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Avatar className="w-14 h-14 ring-2 ring-[#FFF0EC]">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-[#D95D39] text-white">{user.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg font-semibold text-[#2C2623] truncate">{user.name}</div>
                    <div className="text-xs text-[#6E655F]">
                      {user.age ? `${user.age} · ` : ""}{user.location || "Somewhere lovely"}
                    </div>
                  </div>
                </div>
                {user.bio && <p className="text-sm text-[#6E655F] line-clamp-2 mb-3">{user.bio}</p>}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => swipe(user.user_id, "pass")}
                    data-testid={`liked-me-pass-${user.user_id}`}
                    className="flex-1 rounded-full">
                    <X className="w-4 h-4 mr-1" /> Pass
                  </Button>
                  <Button onClick={() => swipe(user.user_id, "like")}
                    data-testid={`liked-me-like-${user.user_id}`}
                    className="flex-1 rounded-full bg-[#D95D39] hover:bg-[#C24C29]">
                    <Heart className="w-4 h-4 mr-1" /> Like back
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
